import { McapIndexedReader, McapTypes } from "@mcap/core";
import { default as PolyfilledSet } from "core-js/actual/set";
import { Heap } from "heap-js";

import { isAbortError } from "@/shared/errors";
import { type Readable } from "@/shared/fs";
import { yieldToEventLoop } from "@/shared/synchronization";

import type { Message, MessageData } from "../Message";
import { MessageFilter } from "../MessageFilter";

import { loadDecompressHandlers } from "./decompressHandlers";
import { McapParsingError } from "./exceptions";
import { type McapChunkIndexLike } from "./MessageChunk";
import { MessageChunks } from "./MessageChunks";
import { type ReaderLike } from "./ReaderLike";
import { makeDeserializer, type Deserializer } from "./serde";

interface InitializationOptions {
  cache?: boolean;
}

interface BaseOptions {
  topics: string[];
  signal: AbortSignal;
}

interface LatestMessageAsOfTimeOptions extends BaseOptions {
  bufferDurationNs?: bigint;
}

interface LoadMessagesOptions extends BaseOptions {
  startTime: bigint;
  endTime: bigint;
}

export class McapReader<D = MessageData> implements ReaderLike<D> {
  #channelInfoById: Map<
    number,
    {
      // Channel definition
      channel: McapTypes.Channel;
      // Channel schema
      schema: McapTypes.Schema;
      // Deserializer for messages within this channel
      deserializer: Deserializer;
    }
  > = new Map();
  #file: Readable;
  #chunks: MessageChunks<D>;
  #options: InitializationOptions;
  #reader: McapIndexedReader;

  static async forFile(
    file: Readable,
    options?: InitializationOptions & { abortSignal?: AbortSignal },
  ): Promise<McapReader> {
    const decompressHandlers = await loadDecompressHandlers();
    options?.abortSignal?.throwIfAborted();

    const reader = await McapIndexedReader.Initialize({
      readable: file,
      decompressHandlers,
    });
    options?.abortSignal?.throwIfAborted();

    return new McapReader(file, reader, {
      cache: options?.cache ?? true,
    });
  }

  constructor(
    file: Readable,
    reader: McapIndexedReader,
    options?: InitializationOptions,
  ) {
    this.#chunks = new MessageChunks(reader.chunkIndexes);
    this.#file = file;
    this.#options = {
      cache: options?.cache ?? true,
    };
    this.#reader = reader;

    for (const [index, channel] of reader.channelsById) {
      const schema = reader.schemasById.get(channel.schemaId);
      if (schema === undefined) {
        throw new McapParsingError(
          `Schema not found for channel ${channel.topic} (id: ${channel.schemaId})`,
        );
      }
      this.#channelInfoById.set(index, {
        channel,
        schema,
        deserializer: makeDeserializer(channel, schema),
      });
    }

  }

  public get file(): Readable {
    return this.#file;
  }

  public get startTime(): bigint | undefined {
    return this.#reader.statistics?.messageStartTime;
  }

  public get endTime(): bigint | undefined {
    return this.#reader.statistics?.messageEndTime;
  }

  public get chunkIndices(): readonly McapTypes.ChunkIndex[] {
    return this.#reader.chunkIndexes;
  }

  public getLatestMessageAsOfTime(time: bigint): Message<D> | undefined {
    const chunk = this.#chunks.getChunkAsOfTime(time);
    if (chunk === undefined || !chunk.isLoaded()) {
      return;
    }
    return chunk.getMessageAsOfTime(time);
  }

  public async loadMessageAsOfTime(
    time: bigint,
    options?: Partial<LatestMessageAsOfTimeOptions>,
  ): Promise<Message<D> | undefined> {
    const chunk = this.#chunks.getChunkAsOfTime(time);
    if (chunk === undefined) {
      return;
    }
    if (chunk.requiresLoad()) {
      await this.loadMessages({
        startTime: chunk.start,
        endTime: chunk.end + (options?.bufferDurationNs ?? 0n),
        topics: options?.topics,
        signal: options?.signal,
      });
    }
    if (chunk.isLoading()) {
      const loaded = await chunk.waitUntilLoaded(options?.signal);
      if (!loaded) {
        return;
      }
    }
    return chunk.getMessageAsOfTime(time);
  }

  public async loadMessages(
    options?: Partial<LoadMessagesOptions>,
  ): Promise<Message<D>[]> {
    const topicStartTime = this.startTime;
    const topicEndTime = this.endTime;

    const startTime = options?.startTime ?? topicStartTime;
    const endTime = options?.endTime ?? topicEndTime;

    const chunks = this.#chunks.getChunksInRange({
      start: startTime,
      end: endTime,
    });

    // Load chunks in batches, yielding to the event loop between each batch
    // to allow other tasks to run (like those that would set an abort signal)
    const unloadedChunks = chunks.filter((chunk) => chunk.requiresLoad());
    while (unloadedChunks.length > 0) {
      options?.signal?.throwIfAborted();

      const batch = unloadedChunks.splice(0, 3);
      await Promise.allSettled(
        batch.map(async (chunk) => {
          options?.signal?.throwIfAborted();
          chunk.beginLoading();
          try {
            const messageStream = this.#reader.readMessages({
              topics: options?.topics,
              startTime: chunk.start,
              endTime: chunk.end,
            });

            const messages = [];
            for await (const message of messageStream) {
              options?.signal?.throwIfAborted();

              const channelInfo = this.#channelInfoById.get(message.channelId);
              if (channelInfo === undefined) {
                // This should only happen in the case of an improperly written MCAP file
                chunk.loadingDidError();
                return;
              }
              const data = channelInfo.deserializer(message.data) as D;
              messages.push({
                data,
                logTime: message.logTime,
                publishTime: message.publishTime,
                sequence: message.sequence,
              });
            }

            chunk.messagesLoaded(messages);
          } catch (err) {
            if (isAbortError(err)) {
              chunk.unload();
              return;
            }
            chunk.loadingDidError();
          }
        }),
      );
    }
    options?.signal?.throwIfAborted();

    // Wait for chunks that are currently loading, however unlikely.
    const loadingChunks = chunks.filter((chunk) => chunk.isLoading());
    if (loadingChunks.length > 0) {
      await Promise.all(
        loadingChunks.map((chunk) => chunk.waitUntilLoaded(options?.signal)),
      );
      options?.signal?.throwIfAborted();
    }

    const messages = chunks.reduce((messages, chunk) => {
      return messages.concat(
        chunk.getMessagesInRange({ start: startTime, end: endTime }),
      );
    }, [] as Message<D>[]);

    if (!this.#options.cache) {
      // Unload messages from chunk if we're not caching messages
      chunks.forEach((chunk) => chunk.unload());
    }

    return messages;
  }

  public async *iterMessageChunks(
    options?: Partial<
      BaseOptions & { filter: MessageFilter<bigint>; validateCrcs?: boolean }
    >,
  ): AsyncGenerator<Message<D>[]> {
    const filter = options?.filter;
    const chunks = this.getFilteredChunks(filter);

    for (const chunk of chunks) {
      await yieldToEventLoop(options?.signal);

      const messageStream = this.#reader.readMessages({
        topics: options?.topics,
        startTime: chunk.start,
        endTime: chunk.end,
        validateCrcs: options?.validateCrcs,
      });

      const messages = [];
      for await (const message of messageStream) {
        options?.signal?.throwIfAborted();

        if (filter !== undefined && !filter.matches(message.logTime)) {
          // Avoid parsing data if we can
          continue;
        }
        const channelInfo = this.#channelInfoById.get(message.channelId);
        if (channelInfo === undefined) {
          // This should only happen in the case of an improperly written MCAP file
          throw new Error(
            "Encountered an error reading topic data. Please reach out to Roboto support.",
          );
        }
        const data = channelInfo.deserializer(message.data) as D;
        messages.push({
          data,
          logTime: message.logTime,
          publishTime: message.publishTime,
          sequence: message.sequence,
        });
      }

      yield messages;
    }
  }

  /**
   * Yields batches of message indices, sorted by logTime.
   */
  public async *iterMessageChunksIndices(
    options?: Partial<BaseOptions & { filter: MessageFilter<bigint> }>,
  ): AsyncGenerator<[bigint, number][]> {
    const batchSize = 1000;
    const filter = options?.filter;
    const chunks = this.getFilteredChunks(filter);
    for (const chunk of chunks) {
      await yieldToEventLoop(options?.signal);

      const messageStream = this.#reader.readMessages({
        topics: options?.topics,
        startTime: chunk.start,
        endTime: chunk.end,
        validateCrcs: false,
      });
      const index: [bigint, number][] = [];
      for await (const message of messageStream) {
        options?.signal?.throwIfAborted();
        if (filter !== undefined && !filter.matches(message.logTime)) {
          continue;
        }
        index.push([message.logTime, message.sequence]);

        if (index.length >= batchSize) {
          yield index;
          index.length = 0;
        }
      }

      yield index;
    }
  }

  private getFilteredChunks(
    filter: MessageFilter<bigint> | undefined,
  ): Heap<McapChunkIndexLike> {
    const topicStartTime = this.startTime;
    const topicEndTime = this.endTime;
    const chunks = new Heap<McapChunkIndexLike>((a, b) => a.index - b.index);

    if (filter === undefined) {
      chunks.addAll(
        this.#chunks.getChunksInRange({
          start: topicStartTime,
          end: topicEndTime,
        }),
      );
    } else if (filter.isRangeFilter()) {
      chunks.addAll(
        this.#chunks.getChunksInRange({
          start: filter.min,
          end: filter.max,
        }),
      );
    } else if (filter.isListFilter()) {
      chunks.addAll(
        this.#chunks.getChunksContainingTimestamps(filter.matching),
      );
    } else if (filter.isCombinedFilter()) {
      // Intersect the chunks returned by the filters
      const chunkSets = filter.filters.map(
        (subFilter) =>
          new PolyfilledSet(this.getFilteredChunks(subFilter).toArray()),
      );

      chunkSets.sort((a, b) => a.size - b.size);
      const intersection = chunkSets.reduce((accum, chunk) => {
        return accum.intersection(chunk);
      });

      chunks.addAll(Array.from(intersection));
    } else {
      throw new Error("Unknown message filter type");
    }

    // Merge adjacent chunks that overlap to avoid reading the same messages multiple times.
    const compacted = new Heap<McapChunkIndexLike>((a, b) => a.index - b.index);

    let current: McapChunkIndexLike | undefined = chunks.pop();
    while (current !== undefined) {
      const next = chunks.pop();
      if (next === undefined) {
        compacted.push(current);
        break;
      }

      // Check if chunks overlap
      if (next.start <= current.end) {
        // Merge: extend current to cover both chunks
        current = {
          start: current.start,
          end: current.end > next.end ? current.end : next.end,
          index: current.index, // Keep the lower index for ordering
        };
      } else {
        // No overlap, push current and move to next
        compacted.push(current);
        current = next;
      }
    }

    return compacted;
  }
}
